import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios from "axios";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import DailyHadith from "./components/DailyHadith";
import QuranReaderWithSync from "./components/QuranReaderWithSync";
import RamadanCalendar from "./components/RamadanCalendar";
import VideoGallery from "./components/VideoGallery";
import AnnouncementsSection from "./components/AnnouncementsSection";
import DonationSection from "./components/DonationSection";
import AboutSection from "./components/AboutSection";
import ContactFooter from "./components/ContactFooter";
import AdminPanel from "./components/AdminPanel";
import PrayerNotifications from "./components/PrayerNotifications";
import TasbihCounter from "./components/TasbihCounter";
import QiblaCompass from "./components/QiblaCompass";
import IslamicCalendar from "./components/IslamicCalendar";
import DuaCollection from "./components/DuaCollection";
import AudioLectures from "./components/AudioLectures";
import NotificationCenter from "./components/NotificationCenter";
import FloatingMenu from "./components/FloatingMenu";
import InstallPrompt from "./components/InstallPrompt";
import NotificationPrompt from "./components/NotificationPrompt";
import { PrivacyPolicy, TermsOfService } from "./components/LegalPages";
import ShkrimeFetare from "./components/ShkrimeFetare";
import ArticlePage from "./components/ArticlePage";
import ArticlesPage from "./components/ArticlesPage";
import VideosPage from "./components/VideosPage";
import "./App.css";

// Home page with all main sections
const HomePage = ({ isLoggedIn, token }) => (
  <main>
    <section id="ballina">
      <Hero />
    </section>

    <DailyHadith />
    <QuranReaderWithSync />
    <DuaCollection preview={true} />
    <ShkrimeFetare />
    <VideoGallery isAdmin={isLoggedIn} token={token} />
    <AnnouncementsSection isAdmin={isLoggedIn} token={token} />
    <DonationSection />
    <AboutSection />
  </main>
);

// Ramazani page
const RamazaniPage = () => (
  <main className="pt-20">
    <RamadanCalendar />
  </main>
);

function App() {
  const [adminOpen, setAdminOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(null);
  const [tasbihOpen, setTasbihOpen] = useState(false);
  const [qiblaOpen, setQiblaOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [duaOpen, setDuaOpen] = useState(false);
  const [audioOpen, setAudioOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // PWA Install Prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
    };
  }, []);

  const handleLogin = (authToken, username) => {
    setToken(authToken);
    setIsLoggedIn(true);
    localStorage.setItem("adminToken", authToken);
  };

  const handleLogout = () => {
    setToken(null);
    setIsLoggedIn(false);
    localStorage.removeItem("adminToken");
  };

  // Check for existing token on mount and handle 401s globally
  React.useEffect(() => {
    const currentToken = localStorage.getItem("adminToken");
    if (currentToken) {
      try {
        const payload = JSON.parse(atob(currentToken.split('.')[1]));
        if (payload.exp * 1000 < Date.now()) {
          // Token expired natively
          localStorage.removeItem("adminToken");
          setToken(null);
          setIsLoggedIn(false);
          toast.info("Sesioni ka skaduar. Ju lutem kyçuni përsëri.");
        } else {
          setToken(currentToken);
          setIsLoggedIn(true);
        }
      } catch (e) {
        localStorage.removeItem("adminToken");
        setToken(null);
        setIsLoggedIn(false);
      }
    }

    // Setup global axios interceptor for any 401 responses
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          const hadToken = !!localStorage.getItem("adminToken");
          if (hadToken) {
            localStorage.removeItem("adminToken");
            setToken(null);
            setIsLoggedIn(false);
            toast.error("Sesioni juaj nuk është i vlefshëm. Ju lutem kyçuni përsëri.");
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  return (
    <BrowserRouter>
      <div className="App min-h-screen bg-background">
        <Toaster position="top-right" richColors />

        <NotificationPrompt />
        <InstallPrompt deferredPrompt={deferredPrompt} />

        {/* Notification Center - Bell Icon */}
        <NotificationCenter
          isOpen={notifOpen}
          onClose={() => setNotifOpen(false)}
          deferredPrompt={deferredPrompt}
          setDeferredPrompt={setDeferredPrompt}
        />

        <Navbar />

        <Routes>
          <Route
            path="/"
            element={<HomePage isLoggedIn={isLoggedIn} token={token} />}
          />
          <Route path="/shkrime/:slug" element={<ArticlePage />} />
          <Route path="/artikuj" element={<ArticlesPage />} />
          <Route path="/videogaleri" element={<VideosPage />} />
          <Route path="/duate" element={<DuaCollection />} />
          <Route path="/ligjerate" element={<AudioLectures />} />
          <Route path="/ramazani" element={<RamazaniPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
        </Routes>

        <ContactFooter />

        {/* Prayer Notifications Toggle */}
        <PrayerNotifications />

        {/* Floating Action Buttons - Expandable Menu for Mobile */}
        <FloatingMenu
          notifOpen={() => setNotifOpen(true)}
          audioOpen={() => setAudioOpen(true)}
          duaOpen={() => setDuaOpen(true)}
          tasbihOpen={() => setTasbihOpen(true)}
          qiblaOpen={() => setQiblaOpen(true)}
          calendarOpen={() => setCalendarOpen(true)}
          adminOpen={() => setAdminOpen(true)}
        />

        {/* Modals */}
        <TasbihCounter
          isOpen={tasbihOpen}
          onClose={() => setTasbihOpen(false)}
        />
        <QiblaCompass isOpen={qiblaOpen} onClose={() => setQiblaOpen(false)} />
        <IslamicCalendar
          isOpen={calendarOpen}
          onClose={() => setCalendarOpen(false)}
        />
        <DuaCollection
          isOpen={duaOpen}
          onClose={() => setDuaOpen(false)}
          isAdmin={isLoggedIn}
          token={token}
        />
        <AudioLectures
          isOpen={audioOpen}
          onClose={() => setAudioOpen(false)}
          isAdmin={isLoggedIn}
          token={token}
        />

        <AdminPanel
          isOpen={adminOpen}
          onClose={() => setAdminOpen(false)}
          onLogin={handleLogin}
          onLogout={handleLogout}
          isLoggedIn={isLoggedIn}
          token={token}
        />
      </div>
    </BrowserRouter>
  );
}

export default App;
